import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BarChart3,
  Brain,
  Briefcase,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  ClipboardList,
  Copy,
  DollarSign,
  ExternalLink,
  Globe,
  Hash,
  Link2,
  Linkedin,
  Mail,
  MapPin,
  Monitor,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  User,
  Users,
  UserSearch,
} from "lucide-react";
import type { MouseEvent, ReactNode } from "react";

import { LeadScore } from "./leads/LeadScore";
import CollapsibleSection from "./ui/CollapsibleSection";
import SectionHeader from "@/components/ui/SectionHeader";
import useApi from "./api";
import { formatDate } from "./utils/dateFormatter";
import {
  type IdentityApiResponse,
  type IdentityField,
  type IdentitySourceBadge,
  type UnifiedVisitorIdentity,
  type VisitorIdentityLead,
  type VisitorProfilePayload,
  formatIdentityFieldValue,
  getIdentityDisplayName,
  getIdentitySubtitle,
  getInitials,
  getOutcomeDisplayLabel,
  isEmailLike,
  isUrlLike,
} from "../lib/visitorIdentity";

type BrowsingPageView = {
  page: string;
  timestamp: string;
};

type BrowsingClickEvent = {
  parent_window_url: string;
  timestamp: string;
};

type BrowsingPageVisitCount = {
  url: string;
  path?: string | null;
  count: number;
  last_visited_at?: string | null;
};

type BrowsingActivityData = {
  pageViews?: BrowsingPageView[];
  clickEvents?: BrowsingClickEvent[];
  pageVisitCounts?: BrowsingPageVisitCount[];
  currentPage?: string | null;
  online?: boolean;
};

type VisitorIdentityProps = {
  conversationId?: string;
  leadId?: string;
  lead?: VisitorIdentityLead | null;
  preloadedIdentity?: UnifiedVisitorIdentity | null;
  browsingActivity?: BrowsingActivityData | null;
  clickable?: boolean;
  mode?: "compact" | "full";
  showLeadEvaluation?: boolean;
  /** Match all full contact-page sections to the prominent objective header style. */
  contactDetailHeadings?: boolean;
  /**
   * Visual treatment for the detail sections.
   *  - "card" (default): each section is its own bordered card. Best on wide
   *    surfaces like the contact profile page.
   *  - "panel": borderless, collapsible sections separated by dividers, with no
   *    row side-padding. Best in narrow side panels (e.g. session details) where
   *    repeated card padding wastes horizontal space.
   */
  variant?: "card" | "panel";
  /** Prefix for per-section localStorage keys so collapse state survives the
   *  remounts that live-refreshing panels trigger. Only used in panel variant. */
  sectionStateKey?: string;
  /** Panel-variant slots: let a parent inject its own sections at fixed points
   *  in the section order so everything reads as one continuous accordion.
   *  - slotAfterSummary:   immediately below the contact summary, above everything else
   *  - slotAfterCollected: after Contact Details / Collected Fields / Questions / AI Labels
   *  - slotAfterBrowsing:  after Browsing Activity / Most Visited Pages
   *  - slotEnd:            after the technical-context sections, at the very bottom */
  slotAfterSummary?: ReactNode;
  slotAfterCollected?: ReactNode;
  slotAfterBrowsing?: ReactNode;
  slotEnd?: ReactNode;
};

function withProtocol(value: string): string {
  return value.startsWith("http://") || value.startsWith("https://") ? value : `https://${value}`;
}

function SourceIcon({ icon }: { icon?: string }) {
  switch (icon) {
    case "brain":
      return <Brain className="w-3 h-3" />;
    case "monitor":
      return <Monitor className="w-3 h-3" />;
    case "mail":
      return <Mail className="w-3 h-3" />;
    case "user":
      return <User className="w-3 h-3" />;
    case "badge-check":
      return <BadgeCheck className="w-3 h-3" />;
    default:
      return <Sparkles className="w-3 h-3" />;
  }
}

function getSummaryAvatarClasses(status: UnifiedVisitorIdentity["summary"]["status"]) {
  switch (status) {
    case "contact":
      return "bg-gradient-to-br from-blue-500 to-purple-600";
    case "identified":
      return "bg-gradient-to-br from-emerald-500 to-teal-600";
    default:
      return "bg-gradient-to-br from-slate-500 to-slate-600";
  }
}

function getFieldIcon(field: IdentityField) {
  switch (field.key) {
    case "email":
    case "email_verified":
      return <Mail className="w-3.5 h-3.5 text-muted-foreground" />;
    case "phone":
      return <Phone className="w-3.5 h-3.5 text-muted-foreground" />;
    case "job_title":
    case "title":
    case "industry":
      return <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />;
    case "company":
    case "name":
      return <Building2 className="w-3.5 h-3.5 text-muted-foreground" />;
    case "employee_count":
      return <Users className="w-3.5 h-3.5 text-muted-foreground" />;
    case "revenue":
    case "estimated_revenue":
      return <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />;
    case "first_seen_at":
    case "last_seen_at":
      return <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />;
    case "website_url":
    case "domain":
    case "referrer":
    case "first_seen_url":
    case "last_seen_url":
      return <Globe className="w-3.5 h-3.5 text-muted-foreground" />;
    case "linkedin_url":
      return <Linkedin className="w-3.5 h-3.5 text-muted-foreground" />;
    case "city":
    case "region":
    case "country":
    case "postal":
    case "ip_address":
    case "hostname":
    case "timezone":
      return <MapPin className="w-3.5 h-3.5 text-muted-foreground" />;
    default:
      return <Link2 className="w-3.5 h-3.5 text-muted-foreground" />;
  }
}

function getFieldHref(field: IdentityField): string | null {
  const value = formatIdentityFieldValue(field.value);
  if (field.key === "email" && isEmailLike(value)) {
    return `mailto:${value}`;
  }
  if (
    field.key === "linkedin_url" ||
    field.key === "website_url" ||
    field.key === "domain" ||
    field.key === "referrer" ||
    field.key === "first_seen_url" ||
    field.key === "last_seen_url"
  ) {
    return withProtocol(value);
  }
  if (isUrlLike(value)) {
    return value;
  }
  return null;
}

function formatFieldDisplayValue(field: IdentityField): string {
  if (field.key === "revenue" && typeof field.value === "number") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(field.value);
  }
  if (
    (field.key === "first_seen_at" || field.key === "last_seen_at") &&
    typeof field.value === "string"
  ) {
    return formatDate(field.value, "short") || field.value;
  }
  return formatIdentityFieldValue(field.value);
}

function getSourceColor(kind: string) {
  switch (kind) {
    case "enriched":
      return "text-violet-500";
    case "website":
      return "text-sky-500";
    case "submitted":
      return "text-amber-500";
    case "contact":
      return "text-blue-500";
    case "ai_label":
      return "text-violet-500";
    default:
      return "text-muted-foreground";
  }
}

function getSourceDescription(source: IdentitySourceBadge): string {
  switch (source.kind) {
    case "submitted":
      return "Submitted by visitor";
    case "enriched":
      return "Enriched via email";
    case "website":
      return "Visitor intelligence";
    case "contact":
      return "Contact record";
    case "ai_label":
      return "AI label";
    default:
      return source.label;
  }
}

function SourceIndicator({ source }: { source?: IdentitySourceBadge }) {
  if (!source) return null;

  return (
    <span className={`relative flex-shrink-0 group/source cursor-default ${getSourceColor(source.kind)}`}>
      <SourceIcon icon={source.icon} />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded-md bg-popover border shadow-md text-[11px] text-popover-foreground whitespace-nowrap opacity-0 group-hover/source:opacity-100 transition-opacity z-50">
        {getSourceDescription(source)}
      </span>
    </span>
  );
}

function CopyButton({
  value,
  label,
  className = "",
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      void navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    },
    [value],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? "Copied" : label ? `Copy ${label}` : "Copy"}
      aria-label={copied ? "Copied" : label ? `Copy ${label}` : "Copy"}
      className={`flex-shrink-0 text-muted-foreground/60 hover:text-foreground transition-colors ${className}`}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-500" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

function IdentityRows({
  title,
  fields,
  hideSourceBadges,
  rowPadX,
}: {
  title: string;
  fields: IdentityField[];
  hideSourceBadges: boolean;
  rowPadX: string;
}) {
  return (
    <div className="divide-y">
      {fields.map((field) => {
        const href = getFieldHref(field);
        const displayValue = formatFieldDisplayValue(field);
        const isIdField = field.key === "id" || field.key === "external_id" || field.key === "visitor_id";
        const isLinkLabel = field.key === "linkedin_url" || field.key === "website_url" || field.key === "first_seen_url" || field.key === "last_seen_url";

        if (isLinkLabel && href) {
          return (
            <div key={`${title}-${field.key}`} className={`${rowPadX} py-2.5 flex items-center justify-between gap-4 overflow-hidden`}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-600 hover:underline min-w-0 truncate"
              >
                {getFieldIcon(field)}
                {field.label}
              </a>
              {!hideSourceBadges && <SourceIndicator source={field.source} />}
            </div>
          );
        }

        return (
          <div key={`${title}-${field.key}`} className={`${rowPadX} py-2.5 flex items-center justify-between gap-4`}>
            <div className="min-w-0 overflow-hidden">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0">{getFieldIcon(field)}</span>
                <span className="text-sm font-medium text-muted-foreground truncate">{field.label}</span>
              </div>
            </div>
            <div className="min-w-0 shrink max-w-[50%] flex items-center gap-2">
              {isIdField ? (
                <>
                  <span className="min-w-0 text-xs truncate text-muted-foreground font-mono">{displayValue}</span>
                  <CopyButton value={displayValue} label={field.label} />
                </>
              ) : href ? (
                <a
                  href={href}
                  target={href.startsWith("mailto:") ? undefined : "_blank"}
                  rel={href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                  className="min-w-0 text-sm text-blue-500 hover:text-blue-600 hover:underline truncate"
                >
                  {displayValue}
                </a>
              ) : (
                <span className="min-w-0 text-sm truncate text-foreground">{displayValue}</span>
              )}
              {!hideSourceBadges && <SourceIndicator source={field.source} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IdentitySection({
  title,
  fields,
  hideSourceBadges = false,
  icon,
  variant = "card",
  sectionStateKey,
  contactDetailHeadings = false,
}: {
  title: string;
  fields: IdentityField[];
  hideSourceBadges?: boolean;
  icon?: ReactNode;
  variant?: "card" | "panel";
  sectionStateKey?: string;
  contactDetailHeadings?: boolean;
}) {
  if (!fields.length) return null;

  if (contactDetailHeadings) {
    return (
      <div className="space-y-2">
        <SectionHeader title={title} iconNode={icon} />
        <div className="rounded-xl border bg-card overflow-hidden">
          <IdentityRows title={title} fields={fields} hideSourceBadges={hideSourceBadges} rowPadX="px-4" />
        </div>
      </div>
    );
  }

  if (variant === "panel") {
    return (
      <CollapsibleSection title={title} icon={icon} storageKey={sectionStateKey ? `${sectionStateKey}:${title}` : undefined}>
        <IdentityRows title={title} fields={fields} hideSourceBadges={hideSourceBadges} rowPadX="px-0" />
      </CollapsibleSection>
    );
  }

  return (
    <div className="rounded-xl border bg-card">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        {icon}
      </div>
      <IdentityRows title={title} fields={fields} hideSourceBadges={hideSourceBadges} rowPadX="px-4" />
    </div>
  );
}

function getPagePath(url: string): string {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.replace(/\/$/, "");
    if (!path || path === "") return "Homepage";
    return path.length > 45 ? path.substring(0, 45) + "..." : path;
  } catch {
    return url.length > 45 ? url.substring(0, 45) + "..." : url;
  }
}

function getDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatDayLabel(date: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (getDayKey(date) === getDayKey(today)) return "Today";
  if (getDayKey(date) === getDayKey(yesterday)) return "Yesterday";

  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

const BROWSING_ACTIVITY_CAP = 6;
const VISIT_COUNT_CAP = 10;

function BrowsingActivitySection({
  data,
  variant = "card",
  sectionStateKey,
  contactDetailHeadings = false,
}: {
  data: BrowsingActivityData;
  variant?: "card" | "panel";
  sectionStateKey?: string;
  contactDetailHeadings?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [countsExpanded, setCountsExpanded] = useState(false);
  const isPanel = variant === "panel";
  const padX = isPanel ? "px-0" : "px-4";

  const currentPagePath = useMemo(
    () => (data.currentPage ? getPagePath(data.currentPage) : null),
    [data.currentPage],
  );

  const timeline = useMemo(() => {
    const pageViews = data.pageViews || [];
    const clickEvents = data.clickEvents || [];

    // Page views are the preferred source (explicit navigations).
    // When we only have clicks (common for widgets embedded in SPAs or
    // contexts where the parent-page tracker never emits view_started),
    // synthesize entries from the URLs clicks were fired on.
    const rawEntries: { url: string; timestamp: string }[] =
      pageViews.length > 0
        ? pageViews.map((view) => ({ url: view.page, timestamp: view.timestamp }))
        : clickEvents
            .filter((click) => Boolean(click.parent_window_url))
            .map((click) => ({ url: click.parent_window_url, timestamp: click.timestamp }));

    if (rawEntries.length === 0) return null;

    const sorted = [...rawEntries].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    const entries: { path: string; url: string; timestamp: Date }[] = [];
    for (const entry of sorted) {
      const path = getPagePath(entry.url);
      const ts = new Date(entry.timestamp);
      if (entries.length === 0 || entries[entries.length - 1].path !== path) {
        entries.push({ path, url: entry.url, timestamp: ts });
      } else {
        entries[entries.length - 1].timestamp = ts;
      }
    }

    return entries;
  }, [data]);

  const pageVisitCounts = useMemo(() => {
    const counts = data.pageVisitCounts || [];
    if (counts.length === 0) return [];
    return [...counts].sort((a, b) => b.count - a.count);
  }, [data.pageVisitCounts]);

  // Determine how to surface the "currently viewing" state:
  //  - If the visitor's live current_page matches the last historical entry,
  //    we decorate that last row.
  //  - If it differs (e.g. they came back online on a previously-opened tab),
  //    we append a synthetic row at the bottom so the current state is always
  //    unambiguous without fabricating a fake page-view record.
  const lastTimelineEntry = timeline && timeline.length > 0 ? timeline[timeline.length - 1] : null;
  const hasCurrentPage = currentPagePath !== null && currentPagePath !== undefined;
  const decorateLastRow = Boolean(
    hasCurrentPage && lastTimelineEntry && lastTimelineEntry.path === currentPagePath,
  );
  const appendCurrentRow = Boolean(
    hasCurrentPage && lastTimelineEntry && lastTimelineEntry.path !== currentPagePath,
  );

  const hasTimeline = Boolean(timeline && timeline.length > 0);
  const hasVisitCounts = pageVisitCounts.length > 0;

  if (!hasTimeline && !hasVisitCounts) return null;

  const displayEntries = timeline
    ? expanded
      ? timeline
      : timeline.slice(-BROWSING_ACTIVITY_CAP)
    : [];
  const hasMore = Boolean(timeline && timeline.length > BROWSING_ACTIVITY_CAP);
  const timelineStartIndex = timeline ? timeline.length - displayEntries.length : 0;

  const syntheticCurrentTimestamp = appendCurrentRow
    ? data.online
      ? new Date()
      : lastTimelineEntry?.timestamp ?? new Date()
    : null;
  const syntheticCurrentDayHeader =
    appendCurrentRow &&
    syntheticCurrentTimestamp &&
    lastTimelineEntry &&
    getDayKey(lastTimelineEntry.timestamp) !== getDayKey(syntheticCurrentTimestamp);

  const visibleVisitCounts = countsExpanded
    ? pageVisitCounts
    : pageVisitCounts.slice(0, VISIT_COUNT_CAP);
  const hasMoreCounts = pageVisitCounts.length > VISIT_COUNT_CAP;

  const timelineHeaderCount = timeline ? (
    <span className="text-xs text-muted-foreground">{timeline.length} {timeline.length === 1 ? "page" : "pages"}</span>
  ) : null;

  const timelineBody = (
    <>
          <div className="divide-y">
            {displayEntries.map((entry, i) => {
              const absoluteIndex = timelineStartIndex + i;
              const isLastTimelineEntry =
                timeline !== null && absoluteIndex === timeline.length - 1;
              const isCurrent = isLastTimelineEntry && decorateLastRow;
              const prevEntry = i > 0 ? displayEntries[i - 1] : undefined;
              const showDayHeader =
                i === 0 || (prevEntry && getDayKey(prevEntry.timestamp) !== getDayKey(entry.timestamp));
              const rowBgClass = isCurrent
                ? data.online
                  ? "bg-emerald-500/5"
                  : isPanel
                    ? ""
                    : "bg-muted/40"
                : "";
              return (
                <div key={i}>
                  {showDayHeader && (
                    <div className={`${padX} py-1.5 flex items-center gap-2 ${isPanel ? "" : "bg-muted/30"}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {formatDayLabel(entry.timestamp)}
                      </span>
                    </div>
                  )}
                  <div className={`${padX} ${isCurrent ? "py-2.5" : "py-2"} flex items-center gap-3 ${rowBgClass} ${showDayHeader ? "border-t" : ""}`}>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-12 flex-shrink-0">
                      {entry.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {isCurrent ? (
                      data.online ? (
                        <span className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                        </span>
                      ) : (
                        <span className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-muted-foreground/60" />
                        </span>
                      )
                    ) : (
                      <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className={`text-sm truncate min-w-0 flex-1 ${isCurrent ? "font-medium" : ""}`} title={entry.url}>
                      {entry.path}
                    </span>
                    {isCurrent && (
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${
                          data.online
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {data.online ? "Currently viewing" : "Last seen"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {appendCurrentRow && syntheticCurrentTimestamp && currentPagePath && (
              <div>
                {syntheticCurrentDayHeader && (
                  <div className={`${padX} py-1.5 flex items-center gap-2 ${isPanel ? "" : "bg-muted/30"}`}>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {formatDayLabel(syntheticCurrentTimestamp)}
                    </span>
                  </div>
                )}
                <div
                  className={`${padX} py-2.5 flex items-center gap-3 ${
                    data.online ? "bg-emerald-500/5" : isPanel ? "" : "bg-muted/40"
                  } ${syntheticCurrentDayHeader ? "border-t" : ""}`}
                >
                  <span className="text-[11px] text-muted-foreground tabular-nums w-12 flex-shrink-0 uppercase">
                    {data.online ? "Now" : "—"}
                  </span>
                  {data.online ? (
                    <span className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                  ) : (
                    <span className="relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-muted-foreground/60" />
                    </span>
                  )}
                  <span
                    className="text-sm truncate min-w-0 flex-1 font-medium"
                    title={data.currentPage || currentPagePath}
                  >
                    {currentPagePath}
                  </span>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${
                      data.online
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {data.online ? "Currently viewing" : "Last seen"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className={`w-full ${padX} py-2 text-xs text-muted-foreground hover:text-foreground border-t text-center transition-colors`}
            >
              {expanded ? "Show less" : `Show all ${timeline?.length ?? 0} pages`}
            </button>
          )}
    </>
  );

  const countsBody = (
    <>
          <div className="divide-y">
            {visibleVisitCounts.map((entry, i) => {
              const displayPath = entry.path && entry.path.length > 0 ? getPagePath(entry.path) : getPagePath(entry.url);
              return (
                <div key={`${entry.url}-${i}`} className={`${padX} py-2 flex items-center gap-3`}>
                  <Globe className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm truncate flex-1 min-w-0" title={entry.url}>{displayPath}</span>
                  <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">
                    {entry.count} {entry.count === 1 ? "visit" : "visits"}
                  </span>
                </div>
              );
            })}
          </div>

          {hasMoreCounts && (
            <button
              type="button"
              onClick={() => setCountsExpanded(!countsExpanded)}
              className={`w-full ${padX} py-2 text-xs text-muted-foreground hover:text-foreground border-t text-center transition-colors`}
            >
              {countsExpanded ? "Show less" : `Show all ${pageVisitCounts.length} pages`}
            </button>
          )}
    </>
  );

  return (
    <div className={isPanel ? "space-y-3" : "space-y-4"}>
      {hasTimeline && timeline && (
        contactDetailHeadings ? (
          <div className="space-y-2">
            <SectionHeader title="Browsing activity" icon={Globe} trailing={timelineHeaderCount} />
            <div className="rounded-xl border bg-card overflow-hidden min-w-0">
              {timelineBody}
            </div>
          </div>
        ) : isPanel ? (
          <CollapsibleSection
            title="Browsing Activity"
            rightElement={timelineHeaderCount}
            storageKey={sectionStateKey ? `${sectionStateKey}:Browsing Activity` : undefined}
          >
            {timelineBody}
          </CollapsibleSection>
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden min-w-0">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Browsing Activity
              </h3>
              {timelineHeaderCount}
            </div>
            {timelineBody}
          </div>
        )
      )}

      {hasVisitCounts && (
        contactDetailHeadings ? (
          <div className="space-y-2">
            <SectionHeader title="Most visited pages" icon={BarChart3} />
            <div className="rounded-xl border bg-card overflow-hidden min-w-0">
              {countsBody}
            </div>
          </div>
        ) : isPanel ? (
          <CollapsibleSection
            title="Most Visited Pages"
            icon={<BarChart3 className="w-4 h-4 text-muted-foreground" />}
            storageKey={sectionStateKey ? `${sectionStateKey}:Most Visited Pages` : undefined}
          >
            {countsBody}
          </CollapsibleSection>
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden min-w-0">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Most Visited Pages
              </h3>
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
            </div>
            {countsBody}
          </div>
        )
      )}
    </div>
  );
}

function VisitorProfilesSection({
  profiles,
  variant = "card",
  sectionStateKey,
  contactDetailHeadings = false,
}: {
  profiles: VisitorProfilePayload[];
  variant?: "card" | "panel";
  sectionStateKey?: string;
  contactDetailHeadings?: boolean;
}) {
  if (!profiles.length) return null;

  const isPanel = variant === "panel";
  const padX = isPanel ? "px-0" : "px-4";
  const countLabel = (
    <span className="text-xs text-muted-foreground">
      {profiles.length} {profiles.length === 1 ? "profile" : "profiles"}
    </span>
  );

  const rows = (
    <div className="divide-y">
      {profiles.map((profile) => {
        const visitorId = profile.visitor_id || "Unknown visitor";

        return (
          <div
            key={profile.id || profile.visitor_id}
            className={`${padX} py-2.5 flex items-center justify-between gap-3`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Hash className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-xs font-mono text-foreground truncate" title={visitorId}>
                {visitorId}
              </span>
              {profile.visitor_id && <CopyButton value={profile.visitor_id} label="visitor ID" />}
            </div>
            {profile.source_latest && (
              <span className="text-[10px] uppercase tracking-wider rounded bg-muted px-1.5 py-0.5 text-muted-foreground flex-shrink-0">
                {profile.source_latest}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  if (isPanel) {
    return (
      <CollapsibleSection
        title="Related Visitor Profiles"
        rightElement={countLabel}
        storageKey={sectionStateKey ? `${sectionStateKey}:Related Visitor Profiles` : undefined}
      >
        {rows}
      </CollapsibleSection>
    );
  }

  if (contactDetailHeadings) {
    return (
      <div className="space-y-2">
        <SectionHeader title="Related visitor profiles" icon={Users} trailing={countLabel} />
        <div className="rounded-xl border bg-card overflow-hidden">{rows}</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Related Visitor Profiles
        </h3>
        {countLabel}
      </div>
      {rows}
    </div>
  );
}

export default function VisitorIdentity({
  conversationId,
  leadId,
  lead,
  preloadedIdentity,
  browsingActivity,
  clickable = true,
  mode = "compact",
  showLeadEvaluation = false,
  contactDetailHeadings = false,
  variant = "card",
  sectionStateKey,
  slotAfterSummary,
  slotAfterCollected,
  slotAfterBrowsing,
  slotEnd,
}: VisitorIdentityProps) {
  const isPanel = variant === "panel";
  const { get } = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState<IdentityApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const hasValidInput = Boolean(conversationId || leadId);
  const shouldFetch = hasValidInput && !preloadedIdentity;

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (conversationId) {
      params.set("conversation_id", conversationId);
    } else if (leadId) {
      params.set("lead_id", leadId);
    }
    return params.toString();
  }, [conversationId, leadId]);

  useEffect(() => {
    const fetchIdentity = async () => {
      if (!shouldFetch || !query) return;
      setLoading(true);
      setFetchError(false);
      try {
        const response = await get(`/visitor/identity?${query}`);
        setData(response.data || null);
      } catch (error) {
        setData(null);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    void fetchIdentity();
  }, [get, shouldFetch, query]);

  const identity = preloadedIdentity || data?.identity || null;
  const effectiveLead = (identity?.lead || lead || null) as VisitorIdentityLead | null;
  const accountId =
    identity?.account_id ||
    effectiveLead?.account_id ||
    (identity?.visitor_profile as { account_id?: string | null } | null | undefined)?.account_id ||
    null;
  const fallbackDisplayName = getIdentityDisplayName({ lead: effectiveLead });
  const fallbackSummary = fallbackDisplayName
    ? {
        status: "contact" as const,
        display_name: fallbackDisplayName,
        subtitle: getIdentitySubtitle({ lead: effectiveLead }),
        avatar_text: getInitials(fallbackDisplayName),
        email: effectiveLead?.email || null,
        linkedin_url: null,
        badges: [
          {
            key: "contact",
            label: "Contact",
            kind: "status",
            icon: "user",
            source: effectiveLead?.source || "lead",
          },
        ],
        has_ip_address: false,
      }
    : null;

  const summary = identity?.summary || fallbackSummary;
  const canNavigateToContact = Boolean(clickable && effectiveLead?.id);

  // Prefer the explicit prop when provided; otherwise fall back to the
  // server-provided identity.browsing_activity so contact/session surfaces
  // get the UI for free.
  const effectiveBrowsingActivity = useMemo<BrowsingActivityData | null>(() => {
    const fromIdentity = identity?.browsing_activity;
    if (effectiveLead && fromIdentity) {
      return {
        pageViews: (fromIdentity.page_views || []).map((view) => ({
          page: view.page || view.url || "",
          timestamp: view.timestamp,
        })),
        clickEvents: (fromIdentity.click_events || []).map((click) => ({
          parent_window_url: click.parent_window_url,
          timestamp: click.timestamp,
        })),
        pageVisitCounts: (fromIdentity.page_visit_counts || []).map((entry) => ({
          url: entry.url,
          path: entry.path,
          count: entry.count,
          last_visited_at: entry.last_visited_at,
        })),
        currentPage: fromIdentity.current_page ?? null,
        online: Boolean(fromIdentity.online),
      };
    }
    if (browsingActivity) return browsingActivity;
    if (!fromIdentity) return null;
    return {
      pageViews: (fromIdentity.page_views || []).map((view) => ({
        page: view.page || view.url || "",
        timestamp: view.timestamp,
      })),
      clickEvents: (fromIdentity.click_events || []).map((click) => ({
        parent_window_url: click.parent_window_url,
        timestamp: click.timestamp,
      })),
      pageVisitCounts: (fromIdentity.page_visit_counts || []).map((entry) => ({
        url: entry.url,
        path: entry.path,
        count: entry.count,
        last_visited_at: entry.last_visited_at,
      })),
      currentPage: fromIdentity.current_page ?? null,
      online: Boolean(fromIdentity.online),
    };
  }, [browsingActivity, effectiveLead, identity?.browsing_activity]);

  const visitorIdValue = effectiveLead ? null : identity?.visitor_id || null;
  const contactProfiles = identity?.visitor_profiles || data?.visitor_profiles || [];
  const latestOutcome = effectiveLead?.latest_outcome || identity?.visitor_profile?.latest_outcome || null;
  const latestOutcomeLabel = getOutcomeDisplayLabel(latestOutcome);
  const visitorSummary =
    effectiveLead?.summary && typeof effectiveLead.summary.text === "string" && effectiveLead.summary.text.trim()
      ? effectiveLead.summary.text.trim()
      : typeof identity?.visitor_summary === "string" && identity.visitor_summary.trim()
      ? identity.visitor_summary.trim()
      : typeof identity?.visitor_profile?.visitor_summary === "string" &&
          identity.visitor_profile.visitor_summary.trim()
        ? identity.visitor_profile.visitor_summary.trim()
        : null;

  // Extract domain for favicon from email or company fields
  const faviconDomain = useMemo(() => {
    // Try company fields first (domain, website_url)
    if (identity?.company_fields) {
      for (const f of identity.company_fields) {
        if ((f.key === "domain" || f.key === "website_url") && typeof f.value === "string" && f.value) {
          try {
            const url = f.value.includes("://") ? f.value : `https://${f.value}`;
            return new URL(url).hostname;
          } catch {
            return f.value.replace(/^https?:\/\//, "").split("/")[0];
          }
        }
      }
    }
    // Fall back to email domain
    const email = summary?.email || effectiveLead?.email;
    if (email && email.includes("@")) {
      const domain = email.split("@")[1];
      // Skip common free email providers
      const freeProviders = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com", "icloud.com", "mail.com", "protonmail.com", "zoho.com"];
      if (domain && !freeProviders.includes(domain.toLowerCase())) {
        return domain;
      }
    }
    return null;
  }, [identity?.company_fields, summary?.email, effectiveLead?.email]);

  const [faviconError, setFaviconError] = useState(false);
  const handleFaviconError = useCallback(() => setFaviconError(true), []);

  // Reset error state when domain changes
  useEffect(() => {
    setFaviconError(false);
  }, [faviconDomain]);

  if ((loading || (shouldFetch && data === null && !fetchError)) && !summary) {
    return (
      <div className="space-y-4 animate-pulse">
        {/* Summary header skeleton */}
        <div className="border rounded-xl overflow-hidden">
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-background to-muted/30">
            <div className="w-14 h-14 rounded-full bg-muted flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-16 bg-muted rounded-full" />
              </div>
              <div className="h-4 w-48 bg-muted rounded" />
              <div className="h-4 w-40 bg-muted rounded" />
            </div>
          </div>
        </div>

        {mode === "full" && (
          <>
            {/* Section skeletons — Contact Details, Collected Info, Company, Query Params, Source Context, Location */}
            {[4, 3, 4, 2, 2, 5].map((rowCount, i) => (
              <div key={i} className="rounded-xl border bg-card">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div className="h-4 w-28 bg-muted rounded" />
                  <div className="h-4 w-4 bg-muted rounded" />
                </div>
                <div className="divide-y">
                  {Array.from({ length: rowCount }).map((_, j) => (
                    <div key={j} className="px-4 py-2.5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 bg-muted rounded" />
                        <div className="h-4 w-20 bg-muted rounded" />
                      </div>
                      <div className="h-4 w-32 bg-muted rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Evaluation skeleton */}
            {showLeadEvaluation && (
              <div className="rounded-xl border bg-card p-4">
                <div className="mb-4">
                  <div className="h-4 w-20 bg-muted rounded" />
                </div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-muted rounded" />
                  <div className="h-4 w-3/4 bg-muted rounded" />
                  <div className="h-4 w-1/2 bg-muted rounded" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  const isAnonymousPlaceholder = !summary;
  const effectiveSummary = summary ?? {
    status: "anonymous" as const,
    display_name: "Unknown Visitor",
    subtitle: null,
    avatar_text: null,
    email: null,
    linkedin_url: null,
    badges: [] as IdentitySourceBadge[],
    has_ip_address: Boolean(data?.has_ip_address || identity?.has_ip_address),
  };
  const summaryClasses = getSummaryAvatarClasses(effectiveSummary.status);

  const isEmailVerified = identity?.lead_details.some(
    (f) => f.key === "email_verified" && f.value === true,
  );

  // HMAC identity verification (distinct from email deliverability above).
  // Contact-level: the lead was verified via a signed SDK identify() at some
  // point. The unified identity endpoint's lead payload may omit the field, so
  // fall back to the lead prop (serialized via to_dict) when provided.
  // Session-level: this visitor session presented a valid signature.
  const contactIdentityVerified = Boolean(
    effectiveLead?.identity_verified_at || lead?.identity_verified_at,
  );
  const sessionIdentityVerified = Boolean(identity?.visitor_profile?.identity_verified_at);
  const identityVerifiedTooltip = contactIdentityVerified
    ? "Identity verified via signed SDK identify"
    : sessionIdentityVerified
      ? "Identity verified this session"
      : null;

  const contactDetailsFields = (identity?.lead_details || []).filter(
    (f) => f.key !== "email" && f.key !== "email_verified",
  );

  // Extract key company fields to display inline in the contact card.
  // "name" is included so contacts with only a company name (no enrichment
  // profile) still surface company context below the visitor ID, matching
  // the behavior we show for non-contact identified visitors.
  const CARD_COMPANY_KEYS = new Set([
    "name",
    "linkedin_url",
    "website_url",
    "domain",
    "industry",
    "employee_count",
    "estimated_revenue",
  ]);
  const subtitleLower = (effectiveSummary?.subtitle || "").toLowerCase();
  const cardCompanyFields = (identity?.company_fields || []).filter((f) => {
    if (!CARD_COMPANY_KEYS.has(f.key)) return false;
    // Skip linkedin if already shown in header via summary
    if (f.key === "linkedin_url" && effectiveSummary?.linkedin_url) return false;
    // Skip the company name if the subtitle already mentions it
    // (e.g. subtitle reads "CEO at Acme Corp" — no need to also list "Acme Corp" below).
    if (
      f.key === "name" &&
      typeof f.value === "string" &&
      f.value.trim().length > 0 &&
      subtitleLower.includes(f.value.trim().toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const collectedFields = identity?.collected_fields || [];
  const collectedRegular = collectedFields.filter((f) => !f.field_type || f.field_type === "field");
  const collectedQuestions = collectedFields.filter((f) => f.field_type === "question");
  const collectedAiLabels = collectedFields.filter((f) => f.field_type === "ai_label");

  const sectionEntries = identity
    ? [
        { title: "Contact Details", fields: contactDetailsFields, hideSourceBadges: true, icon: <User className="w-4 h-4 text-muted-foreground" /> },
        { title: "Collected Fields", fields: collectedRegular, hideSourceBadges: false, icon: <ClipboardList className="w-4 h-4 text-muted-foreground" /> },
        { title: "Questions", fields: collectedQuestions, hideSourceBadges: false, icon: <ClipboardList className="w-4 h-4 text-muted-foreground" /> },
        { title: "AI Labels", fields: collectedAiLabels, hideSourceBadges: false, icon: <Sparkles className="w-4 h-4 text-muted-foreground" /> },
        { title: "Query Parameters", fields: identity.query_parameters, hideSourceBadges: false, icon: <Search className="w-4 h-4 text-muted-foreground" /> },
        { title: "Source Context", fields: identity.referrer, hideSourceBadges: false, icon: <ExternalLink className="w-4 h-4 text-muted-foreground" /> },
        { title: "Location Information", fields: identity.location, hideSourceBadges: true, icon: <MapPin className="w-4 h-4 text-muted-foreground" /> },
      ].filter((section) => section.fields.length > 0)
    : [];

  // Panel layout splits the identity sections into a high-signal "collected"
  // group (shown up top, right after the contact summary) and a lower-priority
  // "technical context" group (query params / source / location) shown near the
  // bottom. Slots from the parent are interleaved between these groups.
  const COLLECTED_TITLES = new Set(["Contact Details", "Collected Fields", "Questions", "AI Labels"]);
  const collectedSections = sectionEntries.filter((s) => COLLECTED_TITLES.has(s.title));
  const contextSections = sectionEntries.filter((s) => !COLLECTED_TITLES.has(s.title));

  const identityLoading = !identity && shouldFetch && !fetchError;

  const hasBrowsing = Boolean(
    effectiveBrowsingActivity &&
      (effectiveBrowsingActivity.pageViews?.length ||
        effectiveBrowsingActivity.clickEvents?.length ||
        effectiveBrowsingActivity.pageVisitCounts?.length),
  );
  const browsingNode = hasBrowsing ? (
    <BrowsingActivitySection
      data={effectiveBrowsingActivity as BrowsingActivityData}
      variant={variant}
      sectionStateKey={sectionStateKey}
      contactDetailHeadings={contactDetailHeadings}
    />
  ) : null;

  const renderIdentitySections = (sections: typeof sectionEntries) =>
    sections.map((section) => (
      <IdentitySection
        key={section.title}
        title={section.title}
        fields={section.fields}
        hideSourceBadges={section.hideSourceBadges}
        icon={section.icon}
        variant={variant}
        sectionStateKey={sectionStateKey}
        contactDetailHeadings={contactDetailHeadings}
      />
    ));

  return (
    <div className={isPanel ? "space-y-3" : "space-y-4"}>
      <div className={isPanel ? "overflow-hidden rounded-lg border bg-card" : "border rounded-xl overflow-hidden"}>
        <div
          onClick={canNavigateToContact ? () => navigate(`/contacts/${effectiveLead?.id}`) : undefined}
          className={`flex items-center gap-4 transition-all duration-200 ${isPanel ? "p-3.5" : "p-4 bg-gradient-to-r from-background to-muted/30"} ${canNavigateToContact ? `hover:cursor-pointer ${isPanel ? "hover:bg-muted/30" : "hover:from-muted/20 hover:to-muted/50 hover:shadow-md"} group` : ""}`}
        >
          {faviconDomain && !faviconError ? (
            <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden">
              <img
                src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(faviconDomain)}&sz=128`}
                alt=""
                className="w-full h-full object-cover"
                onError={handleFaviconError}
              />
            </div>
          ) : isAnonymousPlaceholder ? (
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted flex-shrink-0">
              <UserSearch className="w-6 h-6 text-muted-foreground" />
            </div>
          ) : (
            <div className={`flex items-center justify-center w-14 h-14 rounded-full text-white font-semibold text-lg flex-shrink-0 shadow-md ${summaryClasses}`}>
              {effectiveSummary.avatar_text || <User className="w-6 h-6" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold truncate">{effectiveSummary.display_name}</span>
              {identityVerifiedTooltip && (
                <span title={identityVerifiedTooltip} className="flex-shrink-0 text-emerald-500">
                  <ShieldCheck className="w-4 h-4" />
                </span>
              )}
              {effectiveSummary.linkedin_url && (
                <a
                  href={withProtocol(effectiveSummary.linkedin_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="flex-shrink-0 text-[#0A66C2] hover:text-[#004182] transition-colors"
                  title="View LinkedIn Profile"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {effectiveSummary.badges.map((badge: IdentitySourceBadge) => (
                <span
                  key={`${badge.key}-${badge.label}`}
                  title={badge.label}
                  className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                    badge.kind === "status" && badge.key === "contact"
                      ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                      : badge.kind === "status" && badge.key === "identified"
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  <SourceIcon icon={badge.icon} />
                  {badge.label}
                </span>
              ))}
            </div>
            {effectiveSummary.subtitle && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{effectiveSummary.subtitle}</span>
              </div>
            )}
            {effectiveSummary.email && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <a
                  href={`mailto:${effectiveSummary.email}`}
                  onClick={(event) => event.stopPropagation()}
                  className="truncate text-blue-500 hover:text-blue-600 hover:underline"
                >
                  {effectiveSummary.email}
                </a>
                {isEmailVerified && (
                  <span title="Email verified" className="flex-shrink-0 text-emerald-500">
                    <BadgeCheck className="w-4 h-4" />
                  </span>
                )}
                <CopyButton value={effectiveSummary.email} label="email" />
              </div>
            )}
            {(effectiveSummary.status === "anonymous" || isAnonymousPlaceholder) && (
              <p className="text-sm text-muted-foreground mt-1">
                {effectiveSummary.has_ip_address ? "No identification available yet." : "No IP address available for identification context."}
              </p>
            )}
          </div>
          {canNavigateToContact && <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />}
        </div>

        {visitorIdValue && (
          <div className={`border-t py-2.5 flex items-center justify-between gap-4 ${isPanel ? "px-3.5" : "px-4 bg-muted/20"}`}>
            <div className="flex items-center gap-2 shrink-0">
              <Hash className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Visitor ID</span>
            </div>
            <div className="min-w-0 flex items-center gap-2">
              <span className="min-w-0 text-xs truncate text-muted-foreground font-mono" title={visitorIdValue}>{visitorIdValue}</span>
              <CopyButton value={visitorIdValue} label="visitor ID" />
            </div>
          </div>
        )}

        {latestOutcomeLabel && (
          <div className={`border-t py-2.5 flex items-center justify-between gap-4 ${isPanel ? "px-3.5" : "px-4 bg-muted/20"}`}>
            <div className="flex items-center gap-2 shrink-0">
              <Target className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-sm font-medium text-muted-foreground">Latest Outcome</span>
            </div>
            <div className="min-w-0 flex items-center gap-2">
              <span className="text-sm truncate text-foreground" title={latestOutcomeLabel}>
                {latestOutcomeLabel}
              </span>
            </div>
          </div>
        )}

        {mode === "full" && visitorSummary && (
          <div className={`border-t py-3 ${isPanel ? "px-3.5" : "px-4 bg-muted/20"}`}>
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-sm font-medium text-muted-foreground">
                {effectiveLead || effectiveSummary.status === "contact" ? "Contact Summary" : "Visitor Summary"}
              </span>
            </div>
            <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{visitorSummary}</p>
          </div>
        )}

        {/* Company details appended below the card header */}
        {cardCompanyFields.length > 0 && (
          <div className={`border-t py-3 ${isPanel ? "px-3.5" : "px-4 bg-muted/20"}`}>
            <div className="space-y-1.5 text-sm">
              {cardCompanyFields.map((field) => {
                const href = getFieldHref(field);
                const displayValue = formatFieldDisplayValue(field);
                return (
                  <div key={field.key} className="flex items-center gap-1.5 min-w-0">
                    {getFieldIcon(field)}
                    {field.key === "name" && accountId ? (
                      <Link
                        to={`/accounts/${accountId}`}
                        className="text-blue-500 hover:text-blue-600 hover:underline truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {displayValue}
                      </Link>
                    ) : href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-600 hover:underline truncate"
                      >
                        {displayValue}
                      </a>
                    ) : (
                      <span className="truncate">{displayValue}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {isPanel ? (
        /* Panel layout — one continuous accordion with parent-injected slots.
           Order: contact summary → collected group → [slotAfterCollected] →
           browsing → [slotAfterBrowsing] → technical context → profiles →
           [slotEnd]. */
        <>
          {slotAfterSummary}

          {mode === "full" &&
            (identityLoading ? (
              <div className="space-y-3 animate-pulse">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border bg-card px-3.5 py-3">
                    <div className="h-4 w-4 rounded bg-muted" />
                    <div className="h-4 w-28 rounded bg-muted" />
                    <div className="ml-auto h-4 w-4 rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : (
              renderIdentitySections(collectedSections)
            ))}

          {slotAfterCollected}

          {browsingNode}

          {slotAfterBrowsing}

          {mode === "full" && !identityLoading && (
            <>
              {renderIdentitySections(contextSections)}
              {effectiveLead && <VisitorProfilesSection profiles={contactProfiles} variant={variant} sectionStateKey={sectionStateKey} contactDetailHeadings={contactDetailHeadings} />}
            </>
          )}

          {slotEnd}
        </>
      ) : (
        <>
          {browsingNode}

          {mode === "full" && (
            <>
              {/* Show skeleton sections while identity is loading but we have a fallback summary */}
              {identityLoading ? (
                <div className="space-y-4 animate-pulse">
                  {[4, 3, 4, 2, 2, 5].map((rowCount, i) => (
                    <div key={i} className="rounded-xl border bg-card">
                      <div className="px-4 py-3 border-b flex items-center justify-between">
                        <div className="h-4 w-28 bg-muted rounded" />
                        <div className="h-4 w-4 bg-muted rounded" />
                      </div>
                      <div className="divide-y">
                        {Array.from({ length: rowCount }).map((_, j) => (
                          <div key={j} className="px-4 py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <div className="w-3.5 h-3.5 bg-muted rounded" />
                              <div className="h-4 w-20 bg-muted rounded" />
                            </div>
                            <div className="h-4 w-32 bg-muted rounded" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {showLeadEvaluation && (
                    <div className="rounded-xl border bg-card p-4">
                      <div className="mb-4">
                        <div className="h-4 w-20 bg-muted rounded" />
                      </div>
                      <div className="space-y-3">
                        <div className="h-4 w-full bg-muted rounded" />
                        <div className="h-4 w-3/4 bg-muted rounded" />
                        <div className="h-4 w-1/2 bg-muted rounded" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {renderIdentitySections(sectionEntries)}

                  {effectiveLead && <VisitorProfilesSection profiles={contactProfiles} variant={variant} sectionStateKey={sectionStateKey} contactDetailHeadings={contactDetailHeadings} />}

                  {showLeadEvaluation && (effectiveLead?.id || leadId) && (
                    contactDetailHeadings ? (
                      <div className="space-y-2">
                        <SectionHeader title="Evaluation" icon={Sparkles} />
                        <div className="rounded-xl border bg-card p-4">
                          <LeadScore leadId={(effectiveLead?.id || leadId) as string} />
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border bg-card p-4">
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Evaluation</h3>
                        </div>
                        <LeadScore leadId={(effectiveLead?.id || leadId) as string} />
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
